import qs from 'query-string'

import * as axios from 'axios'

import * as detective from './detective.js'
import * as structure from './structure.js'




export type FailResult = {
	name   : string
	message: string
	stack  : string[]

}

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
export type SuccessRestult = void | number | boolean | string | ArrayBuffer | object


export type HttpHeader = string

export type HttpParams = unknown

export type HttpConfig<P>
	= Omit<axios.AxiosRequestConfig<never>, 'url' | 'method' | 'params' | 'data'>
	& {
		params: P

	}

export type HttpBody<T> = structure.Replace<T, Date, string>

export type HttpBodyUnpacking
<
	T extends SuccessRestult,
	H extends string = never,

>
	= Promise<HttpBody<T> >
		& {
			get(key: Lowercase<H>, default_: string): Promise<string>

		}




export function is_hostname (v: unknown): v is string
{
	if (detective.is_required_string(v) === false)
	{
		return false

	}


	return (/^https?:\/\/(([0-9a-zA-Z]+-)*[0-9a-zA-Z]+)+(\.([0-9a-zA-Z]+-)*[0-9a-zA-Z]+)*(:\d+)?(\/([0-9a-zA-Z]+-?)*[0-9a-zA-Z]+)*\/?$/).test(v)

}


export function is_fail_result (v: unknown): v is FailResult
{
	if (detective.is_object(v) === false)
	{
		return false

	}

	let name = detective.is_string(v.name)
	let message = detective.is_string(v.message)
	let stack = detective.is_array(v.stack) && v.stack.every(detective.is_string)

	return name && message && stack

}


export function is_axios_headers (v: unknown): v is axios.AxiosHeaders
{
	return v instanceof axios.AxiosHeaders

}


export function create (): axios.AxiosInstance
{
	return axios.default
		.create(
			{
				// eslint-disable-next-line @typescript-eslint/naming-convention
				baseURL         : process.env.PUBLIC_HOSTNAME as string,
				// eslint-disable-next-line @typescript-eslint/naming-convention
				paramsSerializer: {
					serialize (params): string
					{
						return qs.stringify(
							// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
							JSON.parse(JSON.stringify(params) ),

							// eslint-disable-next-line @typescript-eslint/naming-convention
							{ arrayFormat: 'comma' },

						)

					},


				},


			},

		)

}


export class Http
{
	#instance: axios.AxiosInstance = create()

	#token = ''

	get token (): string
	{
		return this.#token

	}

	set token (token: string)
	{
		this.#token = token

	}

	get now (): Date
	{
		return Http.now

	}


	#assert
	(
		message: string,

		option: {
			code  : number
			method: string
			url   : string
			data? : unknown

		},

	)
	:	HttpError
	{
		let e = new HttpError(message)

		let stack = [
			`${e.name}: ${message}`,

			`	at code  : ${option.code}`,

			`	at method: ${option.method}`,

			`	at url   : ${option.url}`,

			`	at new HttpError (delta-x-ui)`,

		]


		if (is_fail_result(option.data) )
		{
			e.name = option.data.name
			e.message = option.data.message

			stack = [
				`${e.name}: ${e.message}`,

				...stack.slice(1),

			]

			if (option.data.stack.length > 0)
			{
				stack = option.data.stack

			}

		}

		e.stack = stack.join('\n')

		return e

	}

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	#with_resolvers ()
	{
		type Resolver = {
			headers?: unknown

			set(h: unknown): void

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			promise: HttpBodyUnpacking<any, any>

			resolve(value: unknown): void
			reject(reason?: unknown): void

		}


		let now = Date.now()

		let resolver = Promise.withResolvers() as Resolver

		resolver.set = function set (h: unknown)
		{
			this.headers = h

			if (is_axios_headers(h) )
			{
				Http.mark(now, h.get('date') as string ?? '')

			}


		}

		resolver.promise.get = async function get (key: string, default_: string)
		{
			await this

			let v: null | string = null

			if (is_axios_headers(resolver.headers) )
			{
				v = resolver.headers.get(key) as string

			}

			if (detective.is_exist(v) )
			{
				return v

			}

			return default_

		}

		return resolver

	}

	#request
	<
		T extends SuccessRestult,
		H extends HttpHeader = never,

	>
	(
		options: axios.AxiosRequestConfig,

	)
	:	HttpBodyUnpacking<T, H>
	{
		let resolver = this.#with_resolvers()

		this.#instance
			.request(options)
			.then(
				r =>
				{
					resolver.set(r.headers)

					resolver.resolve(r.data as HttpBody<T>)


				},

			)
			.catch(
				// eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable
				e =>
				{
					let stack = {
						code  : 503,
						header: options.headers ?? {},
						method: options.method!,
						url   : options.url!,
						data  : null as unknown,

					}

					if (axios.isAxiosError<FailResult>(e) )
					{
						let r = e.response!

						stack.code = r.status
						stack.data = r.data

						resolver.set(r.headers)

					}

					resolver.reject(
						this.#assert('request failed', stack),

					)

				},

			)


		return resolver.promise as HttpBodyUnpacking<T, H>


	}

	#authorize
	<
		T extends SuccessRestult,
		H extends HttpHeader = never,

	>
	(
		options: axios.AxiosRequestConfig,

	)
	:	HttpBodyUnpacking<T, H>
	{
		if (detective.is_empty(this.#token) )
		{
			throw this.#assert(
				'token is empty',

				{
					code  : 401,
					method: options.method!,
					url   : options.url!,
					data  : null,

				},

			)

		}

		return this.#request<T, H>(
			{
				...options,

				headers: {
					// eslint-disable-next-line @typescript-eslint/no-misused-spread
					...options.headers,

					// eslint-disable-next-line @typescript-eslint/naming-convention
					Authorization: `Bearer ${this.#token}`,

				},

			},

		)

	}


	create
	<
		T extends SuccessRestult,
		H extends HttpHeader = never,

	>
	(
		options: axios.AxiosRequestConfig,

	)
	:	HttpBodyUnpacking<T, H>
	{
		return this.#request(options)

	}


	options
	<
		H extends HttpHeader = never,
		T extends SuccessRestult = void,

	>
	(
		url: string,

		config?: HttpConfig<never>,

	)
	:	HttpBodyUnpacking<T, H>
	{
		return this.#authorize(
			{ ...config, method: 'options', url },

		)

	}


	get
	<
		P extends HttpParams = never,
		H extends HttpHeader = never,
		T extends SuccessRestult = void,

	>
	(
		url: string,

		config?: HttpConfig<P>,

	)
	:	HttpBodyUnpacking<T, H>
	{
		return this.#authorize(
			{ ...config, method: 'get', url },

		)

	}


	post
	<
		H extends HttpHeader = never,
		T extends SuccessRestult = void,

	>
	(
		url: string,

	)
	:	HttpBodyUnpacking<T, H>

	post
	<
		D,
		H extends HttpHeader = never,
		T extends SuccessRestult = void,

	>
	(
		url: string,
		data: D,

		config?: HttpConfig<never>,

	)
	:	HttpBodyUnpacking<T, H>

	post
	(
		url: string,
		data?: unknown,

		config?: HttpConfig<never>,

	)
	:	HttpBodyUnpacking<SuccessRestult, HttpHeader>
	{
		return this.#authorize(
			{ ...config, method: 'post', url, data },

		)

	}


	put
	<
		D,
		H extends HttpHeader = never,
		T extends SuccessRestult = void,

	>
	(
		url: string,
		data: D,

		config?: HttpConfig<never>,

	)
	:	HttpBodyUnpacking<T, H>
	{
		return this.#authorize(
			{ ...config, method: 'put', url, data },

		)

	}


	patch
	<
		D,
		H extends HttpHeader = never,
		T extends SuccessRestult = void,

	>
	(
		url: string,
		data: Partial<D>,

		config?: HttpConfig<never>,

	)
	:	HttpBodyUnpacking<T, H>
	{
		return this.#authorize(
			{ ...config, method: 'patch', url, data },

		)

	}


	delete
	<
		P extends HttpParams = never,
	>
	(
		url: string,

		config?: HttpConfig<P>,

	)
	:	HttpBodyUnpacking<void, never>
	{
		return this.#authorize(
			{ ...config, method: 'delete', url },

		)

	}




	static #offset = 0

	static #latest = new Date()

	static get now (): Date
	{
		return new Date(this.#offset + Date.now() )

	}

	static get latest (): Date
	{
		return new Date(this.#latest)

	}

	static mark (now: number, latest: string | number): void
	{
		let l = new Date(latest)

		this.#latest = l
		this.#offset = Math.floor(l.valueOf() - now)

	}


}


export class HttpError extends Error
{
	name = 'HttpError'

	code = 400

	get exception (): string[]
	{
		if (detective.is_empty(this.stack) )
		{
			return []

		}

		return this.stack.split('\n')

	}


}


export default new Http()
